import { createContext, useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "./UserContext";
import { Plans } from "../components/PricingAndCheckout/types";
import { v4 as uuidv4 } from "uuid";

export const LayoutContext = createContext<{
    isMenuExpanded: boolean;
    toggleMenu: () => void;
    closeMenu: () => void;
    setMenuBackToSavedState: () => void;
    handleResize: (e: React.MouseEvent) => void;
    notifications: Notification[];
    dispatchNotification: (notificationText: string) => void;
    closeNotification: (id: string) => void;
    isPricingPlansDialogOpen: boolean;
    setIsPricingPlansDialogOpen: (isOpen: boolean) => void;
    openPricingDialog: () => void;
    closePricingDialog: () => void;
    isFreeTrialWelcomeDialogOpen: boolean;
    setIsFreeTrialWelcomeDialogOpen: (isOpen: boolean) => void;
    openFreeTrialWelcomeDialog: () => void;
    closeFreeTrialWelcomeDialog: () => void;
    isTrialEndDialogOpen: boolean;
    setIsTrialEndDialogOpen: (isOpen: boolean) => void;
    openTrialEndDialog: () => void;
    closeTrialEndDialog: () => void;
    isRenamingBreadcrumb: boolean;
    setIsRenamingBreadcrumb: (isRenaming: boolean) => void;
}>(null as any);

export type Notification = {
    id: string;
    text: string;
    duplicate: number; // 1 means first, 2 means second, etc.
};
export const NOTIFICATION_LIFETIME = 5000;

interface LayoutProviderProps {
    children: React.ReactNode;
}
export const LayoutProvider = ({ children }: LayoutProviderProps) => {
    const [isMenuExpanded, setIsMenuExpanded] = useState(localStorage.getItem("navExpanded") !== "false");
    const [isPricingPlansDialogOpen, setIsPricingPlansDialogOpen] = useState(false);
    const [isFreeTrialWelcomeDialogOpen, setIsFreeTrialWelcomeDialogOpen] = useState(false);
    const [isTrialEndDialogOpen, setIsTrialEndDialogOpen] = useState(false);
    const [isRenamingBreadcrumb, setIsRenamingBreadcrumb] = useState(false);

    const { user } = useContext(UserContext);

    // Functions for handling the open state of the pricing dialog
    const openPricingDialog = () => {
        setIsPricingPlansDialogOpen(true);
    };
    const closePricingDialog = () => {
        setIsPricingPlansDialogOpen(false);
    };

    // Functions for handling the open state of the trial end dialog
    const openTrialEndDialog = () => {
        setIsTrialEndDialogOpen(true);
    };

    const closeTrialEndDialog = () => {
        // Set a flag in local storage to indicate that the user has seen the trial end dialog after logging in
        localStorage.setItem("hasSeenTrialEndDialogAfterLogin", "true");
        setIsTrialEndDialogOpen(false);
    };

    const openFreeTrialWelcomeDialog = () => {
        // Check in local storage if the user has already seen the free trial welcome dialog
        const hasSeenFreeTrialWelcomeDialog = localStorage.getItem("hasSeenFreeTrialWelcomeDialog");

        const currentPlan = user?.plan.currentMarketingName as Plans;

        // If the user has already seen the dialog or is not on a free trial plan, do not show it
        if (hasSeenFreeTrialWelcomeDialog === "true" || currentPlan !== Plans.ProfessionalTrial) {
            // If the user has already seen the dialog, do not show it again
            return;
        }
        // Open the free trial welcome dialog
        setIsFreeTrialWelcomeDialogOpen(true);
    };

    const closeFreeTrialWelcomeDialog = () => {
        setIsFreeTrialWelcomeDialogOpen(false);
        // Set a flag in local storage to indicate that the user has seen the free trial welcome dialog
        localStorage.setItem("hasSeenFreeTrialWelcomeDialog", "true");
    };

    useEffect(() => {
        openFreeTrialWelcomeDialog();
    }, [user]);

    // Notification State Variables
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const dispatchNotification = (notificationText: string) => {
        // Find all notifications with the same text and get the highest duplicate value
        let maxDuplicate = 0;
        notifications.forEach(n => {
            if (n.text === notificationText && n.duplicate > maxDuplicate) {
                maxDuplicate = n.duplicate;
            }
        });
        const newDuplicate = maxDuplicate + 1;

        // Remove all previous notifications with the same text
        const filteredNotifications = notifications.filter(n => n.text !== notificationText);

        const newNotification: Notification = {
            id: uuidv4(),
            text: notificationText,
            duplicate: newDuplicate,
        };

        // Add only the new notification with the correct duplicate count
        setNotifications([...filteredNotifications, newNotification]);
    };

    const closeNotification = (id: string) => {
        setNotifications(prevNotifications => prevNotifications.filter(n => n.id !== id));
    };

    // ============ Start of Resizing Logic ============
    const [rightColumnWidth, setRightColumnWidth] = useState<number>(0);
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const startX = useRef<number>(0);
    const startWidth = useRef<number>(0);

    // Function to handle mouse down event when resize starts
    const handleMouseDown = (e: React.MouseEvent) => {
        // Prevent text selection during drag
        e.preventDefault();
        setIsResizing(true);

        startX.current = e.clientX;
        startWidth.current = rightColumnWidth;
    };

    // Function to handle mouse move event when resizing the docs tray
    const handleMouseMove = (e: MouseEvent) => {
        if (!isResizing) return;
        // Calculate how far the mouse has moved
        const deltaX = e.clientX - startX.current;
        // Adjust the new width based on mouse movement
        const newWidth = startWidth.current - deltaX;

        // GSNOTE: Random values that I picked. There are probably better min/max values.
        if (newWidth < 400) {
            setRightColumnWidth(400);
            return;
        }

        if (newWidth > 800) {
            setRightColumnWidth(800);
            return;
        }

        setRightColumnWidth(newWidth);
    };

    // Function to handle mouse up event when resizing stops
    const handleMouseUp = () => {
        setIsResizing(false);
    };

    // Add event listeners to document on mouse move and mouse up
    useEffect(() => {
        if (isResizing) {
            // Remove animation on the right column when resizing
            document.getElementById("app-shell")?.classList.remove("duration-300");
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        } else {
            // Add animation back to the right column when resizing stops. Animation classes are handled this way because if you don't do this, the animation will fire when the user resizes the docs tray and it looks wrong.
            document.getElementById("app-shell")?.classList.add("duration-300");
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        }

        return () => {
            // Cleanup the event listeners when the component is unmounted
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isResizing]);
    // ============ End of Resizing Logic ============

    const toggleMenu = () => {
        setIsMenuExpanded(!isMenuExpanded);
        localStorage.setItem("navExpanded", (!isMenuExpanded).toString());
    };

    const closeMenu = () => {
        setIsMenuExpanded(false);
    };

    const setMenuBackToSavedState = () => {
        setIsMenuExpanded(localStorage.getItem("navExpanded") !== "false");
    };

    return (
        <LayoutContext.Provider
            value={{
                isMenuExpanded,
                toggleMenu,
                closeMenu,
                setMenuBackToSavedState,
                handleResize: handleMouseDown,
                notifications: notifications.map(n => ({
                    ...n,
                    // For display, append (x) if duplicate > 1
                    text: n.duplicate > 1 ? `${n.text} (${n.duplicate})` : n.text,
                })),
                dispatchNotification,
                closeNotification,
                isPricingPlansDialogOpen,
                setIsPricingPlansDialogOpen,
                openPricingDialog,
                closePricingDialog,
                isFreeTrialWelcomeDialogOpen,
                setIsFreeTrialWelcomeDialogOpen,
                openFreeTrialWelcomeDialog,
                closeFreeTrialWelcomeDialog,
                isTrialEndDialogOpen,
                setIsTrialEndDialogOpen,
                openTrialEndDialog,
                closeTrialEndDialog,
                isRenamingBreadcrumb,
                setIsRenamingBreadcrumb,
            }}
        >
            {children}
        </LayoutContext.Provider>
    );
};

export const useNotification = () => {
    const { dispatchNotification } = useContext(LayoutContext);
    return { dispatchNotification };
};
